import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Users,
  DollarSign,
  CreditCard,
  Landmark,
  FileText,
  AlertTriangle,
  AlertCircle,
  Info,
  TrendingUp,
  Calendar,
  Clock,
  Sun,
  Heart,
  Gift,
  ChevronLeft,
  ChevronRight,
  Download,
  Database
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [backupLoading, setBackupLoading] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const { t } = useLanguage();

  const fetchData = useCallback(async (month) => {
    try {
      setLoading(true);
      const [statsRes, warningsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats?month=${month}`),
        axios.get(`${API}/warnings`)
      ]);
      setStats(statsRes.data);
      setWarnings(warningsRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData(selectedMonth);
  }, [selectedMonth, fetchData]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3
    }).format(value || 0) + ' TND';
  };

  const formatMonthDisplay = (monthStr) => {
    if (!monthStr) return '';
    const [year, month] = monthStr.split('-');
    const monthNames = {
      de: ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'],
      fr: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']
    };
    return `${monthNames.de[parseInt(month) - 1]} ${year}`;
  };

  const navigateMonth = (direction) => {
    const [year, month] = selectedMonth.split('-').map(Number);
    let newMonth = month + direction;
    let newYear = year;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(`${newYear}-${String(newMonth).padStart(2, '0')}`);
  };

  const handleBackupDownload = async () => {
    setBackupLoading(true);
    try {
      const [year, month] = selectedMonth.split('-').map(Number);
      const response = await axios.get(`${API}/export/backup/monthly?month=${month}&year=${year}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
      const filename = `Backup_${monthNames[month - 1]}_${year}_${new Date().toISOString().split('T')[0]}.xlsx`;
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup erfolgreich heruntergeladen');
    } catch (error) {
      console.error('Backup download error:', error);
      toast.error('Backup Download fehlgeschlagen');
    } finally {
      setBackupLoading(false);
    }
  };

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#E31E24] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const statCards = [
    {
      title: t('totalEmployees'),
      value: stats?.employees?.total_active || 0,
      icon: Users,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      title: t('totalGrossThisMonth'),
      value: formatCurrency(stats?.current_month?.total_gross),
      icon: DollarSign,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      title: t('totalNetThisMonth'),
      value: stats?.current_month?.entries_with_net > 0 
        ? formatCurrency(stats?.current_month?.total_net)
        : t('notYetEntered'),
      icon: DollarSign,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      subtitle: stats?.current_month?.entries_with_net > 0 
        ? `${stats.current_month.entries_with_net}/${stats.current_month.count} ${t('entered')}`
        : null
    },
    {
      title: t('socialTaxThisMonth'),
      value: stats?.current_month?.entries_with_net > 0 
        ? formatCurrency(stats?.current_month?.total_social_tax)
        : '-',
      icon: Landmark,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      subtitle: stats?.current_month?.entries_with_net > 0 
        ? t('grossMinusNet')
        : null
    },
    {
      title: t('openAdvances'),
      value: formatCurrency(stats?.finance?.open_advances),
      icon: CreditCard,
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    },
    {
      title: t('loanBalance'),
      value: formatCurrency(stats?.finance?.active_loans),
      icon: Landmark,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      title: t('draftPayrolls'),
      value: stats?.payrolls?.draft || 0,
      icon: FileText,
      color: 'text-cyan-600',
      bgColor: 'bg-cyan-50'
    },
    {
      title: t('warnings'),
      value: warnings.length || 0,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-50'
    }
  ];

  const getWarningIcon = (type) => {
    switch (type) {
      case 'error': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      default: return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getWarningBadgeClass = (type) => {
    switch (type) {
      case 'error': return 'badge-error';
      case 'warning': return 'badge-warning';
      default: return 'badge-info';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dashboard-page">
      {/* Header with Month Selector */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1A1A1A]">{t('dashboard')}</h1>
          <p className="text-gray-500 mt-1">
            {t('monthlyOverview')}
          </p>
        </div>
        
        {/* Month Selector */}
        <Card className="bg-white border-gray-200 p-2">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateMonth(-1)}
              className="text-gray-500 hover:text-[#E31E24]"
              data-testid="prev-month-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <div className="flex items-center gap-2 px-3">
              <Calendar className="w-5 h-5 text-[#E31E24]" />
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border-0 bg-transparent text-center font-semibold text-[#1A1A1A] w-40"
                data-testid="month-selector"
              />
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateMonth(1)}
              className="text-gray-500 hover:text-[#E31E24]"
              data-testid="next-month-btn"
            >
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </Card>
      </div>

      {/* Selected Month Display */}
      <div className="bg-gradient-to-r from-[#E31E24] to-[#FF6B6B] rounded-lg p-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm opacity-80">{t('selectedPeriod')}</p>
            <h2 className="text-2xl font-bold">{formatMonthDisplay(selectedMonth)}</h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm opacity-90">
              <TrendingUp className="w-4 h-4" />
              <span>{stats?.current_month?.count || 0} {t('finalized')}</span>
            </div>
            <Button
              onClick={handleBackupDownload}
              disabled={backupLoading}
              variant="secondary"
              className="bg-white/20 hover:bg-white/30 text-white border-0"
              data-testid="backup-download-btn"
            >
              {backupLoading ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Backup...
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-2" />
                  Monatliches Backup
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat, index) => (
          <Card 
            key={stat.title}
            className="bg-white border-gray-200 hover:border-gray-300 hover:shadow-md transition-all"
            data-testid={`stat-card-${index}`}
          >
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold text-[#1A1A1A] font-mono">
                    {stat.value}
                  </p>
                  {stat.subtitle && (
                    <p className="text-xs text-gray-400 mt-1">{stat.subtitle}</p>
                  )}
                </div>
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Monthly Details Section */}
      <Card className="bg-white border-gray-200" data-testid="monthly-details-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold text-[#1A1A1A] flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[#E31E24]" />
            {t('monthlyDetails')} - {formatMonthDisplay(selectedMonth)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.current_month?.count === 0 ? (
            <p className="text-gray-400 text-center py-8">{t('noDataForPeriod')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Hours Overview */}
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-blue-800">{t('hoursOverview')}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('regularHours')}:</span>
                    <span className="font-mono font-semibold">{stats?.current_month?.total_regular_hours || 0} h</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('overtimeHours')}:</span>
                    <span className="font-mono font-semibold">{stats?.current_month?.total_overtime_hours || 0} h</span>
                  </div>
                </div>
              </div>

              {/* Leave Overview */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sun className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-800">{t('leaveOverview')}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('vacationDays')}:</span>
                    <span className="font-mono font-semibold">{stats?.current_month?.total_vacation_days || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('sickDays')}:</span>
                    <span className="font-mono font-semibold">{stats?.current_month?.total_sick_days || 0}</span>
                  </div>
                </div>
              </div>

              {/* Bonus Overview */}
              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Gift className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-purple-800">{t('bonusOverview')}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('totalBonus')}:</span>
                    <span className="font-mono font-semibold">{formatCurrency(stats?.current_month?.total_bonus)}</span>
                  </div>
                </div>
              </div>

              {/* Deductions Overview */}
              <div className="bg-amber-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-3">
                  <CreditCard className="w-5 h-5 text-amber-600" />
                  <span className="font-semibold text-amber-800">{t('deductionsOverview')}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('totalDeductions')}:</span>
                    <span className="font-mono font-semibold">{formatCurrency(stats?.current_month?.total_deductions)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Employee List for Month */}
          {stats?.current_month?.employees?.length > 0 && (
            <>
              <Separator className="my-4" />
              <div>
                <h4 className="font-semibold text-[#1A1A1A] mb-3">{t('employeesInPayroll')}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                  {stats.current_month.employees.map((emp, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-gray-50 rounded-md">
                      <span className="text-sm text-gray-700">{emp.name}</span>
                      <span className="font-mono text-sm font-semibold text-[#1A1A1A]">
                        {formatCurrency(emp.gross)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Warnings Section */}
      <Card className="bg-white border-gray-200" data-testid="warnings-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-bold text-[#1A1A1A] flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-[#E31E24]" />
            {t('warnings')} ({warnings.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {warnings.length === 0 ? (
            <p className="text-gray-400 text-center py-8">{t('noData')}</p>
          ) : (
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {warnings.map((warning, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-3 p-3 rounded-md bg-gray-50 border border-gray-200 hover:border-gray-300 transition-colors"
                    data-testid={`warning-item-${index}`}
                  >
                    {getWarningIcon(warning.severity)}
                    <span className="flex-1 text-sm text-[#1A1A1A]">{warning.message}</span>
                    <Badge className={`${getWarningBadgeClass(warning.severity)} text-xs`}>
                      {warning.entity_type}
                    </Badge>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
